import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import * as authApi from '../api/auth';
import clsx from 'clsx';

interface Notification {
  type: 'success' | 'error' | 'info';
  message: string;
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setNotification({
        type: 'error',
        message: '重置链接无效，请重新申请密码重置'
      });
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotification(null);

    if (!token) {
      setNotification({
        type: 'error',
        message: '重置链接无效'
      });
      return;
    }

    if (!newPassword || !confirmPassword) {
      setNotification({
        type: 'error',
        message: '请输入新密码'
      });
      return;
    }

    if (newPassword.length < 6) {
      setNotification({
        type: 'error',
        message: '密码长度至少为6个字符'
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setNotification({
        type: 'error',
        message: '两次输入的密码不一致'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await authApi.resetPassword(token, newPassword, confirmPassword);

      if (result.success) {
        setNotification({
          type: 'success',
          message: '密码重置成功，即将跳转到登录页面'
        });
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || '密码重置失败，请重试';
      setNotification({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '');
    setNewPassword(value);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '');
    setConfirmPassword(value);
  };

  return (
    <div className="min-h-screen flex flex-col bg-stone-50">
      <div className="w-full bg-white border-b border-gray-100">
        <div className="max-w-lg mx-auto px-4 py-6">
          <div className="flex flex-col items-center">
            <img
              src="/logo.png"
              alt="梅花易道书院"
              className="w-full max-w-[529px] aspect-[529/367] object-contain mb-6"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pt-8 pb-12">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">重置密码</h2>
            <p className="text-sm text-gray-600 mb-6">
              请输入您的新密码
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  新密码
                </label>
                <div className="relative">
                  <input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    required
                    className="block w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 shadow-sm focus:ring-red-500 focus:border-red-500"
                    placeholder="请输入新密码（至少6个字符）"
                    value={newPassword}
                    onChange={handlePasswordChange}
                    disabled={!token}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  确认新密码
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    className="block w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 shadow-sm focus:ring-red-500 focus:border-red-500"
                    placeholder="请再次输入新密码"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    disabled={!token}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={!token || isSubmitting}
                className="w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? '重置中...' : '重置密码'}
              </button>
            </form>

            {notification && (
              <div
                className={clsx(
                  'mt-4 text-sm text-center py-3 px-4 rounded-lg',
                  notification.type === 'success' && 'bg-green-50 text-green-600',
                  notification.type === 'error' && 'bg-red-50 text-red-600',
                  notification.type === 'info' && 'bg-blue-50 text-blue-600'
                )}
              >
                {notification.message}
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-red-600 hover:text-red-700"
            >
              返回登录
            </button>
          </div>

          <div className="text-center text-xs text-gray-500 mt-8">
            <p>Copyright©2024-2034 梅花易道书院 版权所有</p>
          </div>
        </div>
      </div>
    </div>
  );
}
